import React, { useEffect, useState } from "react";
import { baseUrl, verify_service_estimate } from "../../API/api";
import { useParams } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { view_service_estimate } from "../../API/api";
import customFetch from "../../API/CustomFetch";
import showToastMessage from "../../Toastify/Index";
import Navbar from "../customer-consent/components/Navbar";

export const formatINR = (v: any) => {
  if (v === null || v === undefined || v === "") return "";

  return Number(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

interface EstimateSpare {
  SpareId: number | null;
  Quantity: number | null;
  PartPrice: number | null;
  FinalPrice: number | null;
  PartNameSnapshot: string | null;
}

interface EstimateLineItem {
  Spares: EstimateSpare[];
  LineItemId: number;
  RepairType: string;
  TechDecision: string;
  ComponentSnapshotName: string;
  ComponentPartsSubtotal: number;
}

export interface EstimateResponse {
  EstimateId: number;
  JobCardId: number;
  EstimateTotal: string;
  Status: string;
  SharedAt?: string;
  CustomerRespondedAt?: string;
  created_at?: string;
  LineItems: EstimateLineItem[];
}

const ServiceShareEstimate: React.FC = () => {
  const { jobCardNo } = useParams<{ jobCardNo: string }>();
  const [estimate, setEstimate] = useState<EstimateResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // OTP States
  const [otp, setOtp] = useState<string>("");
  const [otpVerifying, setOtpVerifying] = useState<boolean>(false);
  const [isOtpVerified, setIsOtpVerified] = useState<boolean>(false);

  const fetchEstimate = async () => {
    if (!jobCardNo) {
      showToastMessage(false, "Jobcard number is null", "false1");
      return;
    }

    setLoading(true);
    try {
      const url = view_service_estimate(jobCardNo);
      const completUrl = `${baseUrl}/${url}`;
      const response = await customFetch(completUrl);

      if (response && response.SUCCESS) {
        setEstimate(response.DATA);
      } else {
        setEstimate(null);
      }
    } catch (error) {
      setEstimate(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEstimate();
  }, [jobCardNo]);

  const handleVerifyOtp = async () => {
    if (!jobCardNo) {
      showToastMessage(false, "Jobcard number missing");
      return;
    }

    if (otp.length !== 6) {
      showToastMessage(false, "Please enter a valid 6-digit OTP");
      return;
    }

    setOtpVerifying(true);
    try {
      const url = `${baseUrl}/${verify_service_estimate(jobCardNo)}`;
      const payload = JSON.stringify({ otp });

      const response = await customFetch(url, payload, { method: "PATCH" });

      if (response && response.SUCCESS) {
        setIsOtpVerified(true);
        showToastMessage(true, "OTP Verified successfully!");
      } else {
        showToastMessage(false, response?.MESSAGE || "OTP Verification failed");
      }
    } catch (error) {
      showToastMessage(false, "Error verifying OTP");
    } finally {
      setOtpVerifying(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-4 max-w-7xl mx-auto text-black">
      <div>
        <Navbar />
        <p className="text-lg sm:text-xl font-bold text-center mt-3 text-black">
          Service Estimate Breakdown
        </p>
      </div>

      {loading ? (
        <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 rounded-2xl border border-gray-300 bg-gray-50/50 py-24 text-sm text-black">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
          <p className="font-medium text-black">Fetching estimate details...</p>
        </div>
      ) : estimate ? (
        <div className="space-y-6">
          <div className="overflow-hidden rounded-2xl border-2 border-gray-300 bg-white shadow-sm">
            {estimate.LineItems.length === 0 ? (
              <div className="p-8 text-center text-sm text-black">
                No line items available for this estimate.
              </div>
            ) : (
              <>
                {/* MOBILE CARD VIEW */}
                <div className="block md:hidden divide-y-2 divide-gray-300">
                  {estimate.LineItems.map((item) => (
                    <div key={item.LineItemId} className="p-4 space-y-3">
                      {/* Component Header & Decision */}
                      <div className="flex items-start justify-between gap-2 border-b border-gray-300 pb-2">
                        <div>
                          <p className="text-xs text-black uppercase font-semibold">
                            Component
                          </p>
                          <p className="text-sm font-semibold text-black">
                            {item.ComponentSnapshotName}
                          </p>
                        </div>
                        <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-[11px] font-semibold text-black border border-gray-300 shrink-0">
                          {item.TechDecision.replace(/_/g, " ")}
                        </span>
                      </div>

                      {/* Spares List */}
                      <div className="space-y-2">
                        {item.Spares.map((spare) => (
                          <div
                            key={spare.SpareId}
                            className="bg-gray-50 p-2.5 rounded-lg border border-gray-300 text-xs space-y-1"
                          >
                            <p className="font-semibold text-black">
                              {spare.PartNameSnapshot}
                            </p>
                            <div className="flex items-center justify-between text-black">
                              <span>
                                Qty:{" "}
                                <strong className="text-black font-semibold">
                                  {spare.Quantity}
                                </strong>{" "}
                                × ₹{formatINR(spare.PartPrice)}
                              </span>
                              <span className="font-semibold text-black text-xs">
                                ₹{formatINR(spare.FinalPrice)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Component Subtotal */}
                      <div className="flex justify-between items-center text-xs font-semibold text-black bg-gray-100 px-3 py-2 rounded-md border border-gray-300">
                        <span>{item.ComponentSnapshotName} Subtotal:</span>
                        <span>₹{formatINR(item.ComponentPartsSubtotal)}</span>
                      </div>
                    </div>
                  ))}

                  {/* Mobile Total */}
                  <div className="p-4 bg-emerald-50 border-t-2 border-emerald-400 flex justify-between items-center text-black">
                    <span className="text-xs font-black uppercase">
                      Total Estimated Cost
                    </span>
                    <span className="text-base font-black">
                      ₹{formatINR(estimate.EstimateTotal)}
                    </span>
                  </div>
                </div>

                {/* DESKTOP TABLE VIEW */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-left text-xs text-black">
                    <thead className="bg-gray-100 text-[11px] uppercase tracking-wider text-black border-b-2 border-gray-300">
                      <tr>
                        <th className="px-5 py-3.5 font-extrabold border-r border-gray-300">
                          Component Details
                        </th>
                        <th className="px-5 py-3.5 font-extrabold border-r border-gray-300">
                          Spare Part Name
                        </th>
                        <th className="px-5 py-3.5 font-extrabold text-center w-20 border-r border-gray-300">
                          Qty
                        </th>
                        <th className="px-5 py-3.5 font-extrabold text-right w-32 border-r border-gray-300">
                          Unit Price
                        </th>
                        <th className="px-5 py-3.5 font-extrabold text-right w-36">
                          Final Price
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-300">
                      {estimate.LineItems.map((item) => (
                        <React.Fragment key={item.LineItemId}>
                          {item.Spares.map((spare, index) => (
                            <tr
                              key={spare.SpareId}
                              className="transition-colors hover:bg-gray-50"
                            >
                              {index === 0 && (
                                <td
                                  rowSpan={item.Spares.length}
                                  className="border-r border-gray-300 px-5 py-4 align-top bg-white"
                                >
                                  <div className="space-y-2">
                                    <p className="text-sm font-semibold text-black">
                                      {item.ComponentSnapshotName}
                                    </p>
                                    <div className="flex flex-wrap items-center gap-1.5">
                                      <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-[11px] font-semibold text-black border border-gray-300">
                                        {item.TechDecision.replace(/_/g, " ")}
                                      </span>
                                    </div>
                                  </div>
                                </td>
                              )}
                              <td className="px-5 py-3.5 font-semibold text-black border-r border-gray-300">
                                {spare.PartNameSnapshot}
                              </td>
                              <td className="px-5 py-3.5 text-center font-semibold text-black border-r border-gray-300">
                                {spare.Quantity}
                              </td>
                              <td className="px-5 py-3.5 text-right font-semibold text-black border-r border-gray-300">
                                ₹{formatINR(spare.PartPrice)}
                              </td>
                              <td className="px-5 py-3.5 text-right font-semibold text-black">
                                ₹{formatINR(spare.FinalPrice)}
                              </td>
                            </tr>
                          ))}
                          {/* Component Subtotal Summary Row */}
                          <tr className="bg-gray-100/70 border-b-2 border-gray-300">
                            <td
                              colSpan={4}
                              className="px-5 py-2.5 text-right text-[11px] font-semibold text-black uppercase tracking-wider border-r border-gray-300"
                            >
                              {item.ComponentSnapshotName} Subtotal:
                            </td>
                            <td className="px-5 py-2.5 text-right text-xs font-black text-black">
                              ₹{formatINR(item.ComponentPartsSubtotal)}
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                    {/* GRAND TOTAL ROW */}
                    <tfoot className="bg-emerald-50/80 border-t-2 border-emerald-400">
                      <tr>
                        <td
                          colSpan={4}
                          className="px-5 py-4 text-right text-sm font-black uppercase tracking-wider text-black border-r border-emerald-200"
                        >
                          <div className="flex items-center justify-start gap-1.5">
                            <span>Total Estimated Cost</span>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-right text-base font-black text-black">
                          ₹{formatINR(estimate.EstimateTotal)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            )}
          </div>
          <p>
            **Prices shown are estimates and exclude labour charges and
            applicable taxes. Labour and taxes will be charged separately. Final
            charges may vary based on the actual work performed.
          </p>

          {/* OTP VERIFICATION SECTION */}
          <div className="rounded-2xl border-2 border-gray-300 bg-white p-5 sm:p-6 shadow-sm">
            {isOtpVerified ? (
              <div className="flex items-center justify-center gap-2 p-4 bg-emerald-50 border border-emerald-300 rounded-xl text-emerald-800 font-bold text-base">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                <span>OTP Verified</span>
              </div>
            ) : (
              <div className="max-w-md mx-auto space-y-4 text-center">
                <p className="text-sm font-bold text-black">
                  Enter 6-digit OTP to verify service estimate
                </p>
                <div className="flex flex-col items-center justify-center gap-3">
                  <input
                    type="text"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    placeholder="Enter 6-digit OTP"
                    className="w-full sm:w-48 px-4 py-2 text-center text-base font-bold tracking-widest border-2 border-gray-500 rounded-lg focus:border-black focus:outline-none text-black"
                  />
                  <button
                    type="button"
                    onClick={handleVerifyOtp}
                    disabled={otp.length !== 6 || otpVerifying}
                    className="w-full sm:w-auto px-6 py-2.5 bg-green-600 text-white font-bold rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                  >
                    {otpVerifying ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin text-white" />
                        <span>Verifying...</span>
                      </>
                    ) : (
                      "Verify OTP"
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border-2 border-gray-300 bg-white p-8 sm:p-12 text-center text-black">
          No estimate data found for job card{" "}
          <span className="font-semibold">{jobCardNo}</span>.
        </div>
      )}
    </div>
  );
};

export default ServiceShareEstimate;
